import _ from 'lodash'
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'

import { changeModal } from 'services/actions'
import { validateField, validateFields } from 'common/validator'
import { postShowcaseService } from 'services/showcase'
import { loadShowcasesSaga } from 'services/showcase/filter/saga'
import { extractHashtags } from 'common/utils/parse-hashtag'
import mixpanel, { EVENTS } from 'lib/mixpanel'

import {
  addFieldError,
  addFieldsErrors,
  clearFieldError,
  SUBMIT_SHOWCASE,
  VALIDATE_SHOWCASE_FIELD,
} from './actions'

interface ShowcaseFormData {
  body?: { value: string }
  fileUrl?: { value: string }
  fileType?: { value: string }
  author?: { value: string }
  isStatic?: { value: boolean }
  id?: { value?: string }
  slug?: { value?: string }
  authorPhotoUrl?: { value?: string }
}

export function* submitShowcaseSaga({
  payload: data,
}: {
  payload: Record<string, { value: unknown }>
}): Generator<unknown, void, unknown> {
  const errors = validateFields(data as Record<string, { value: unknown; validations?: string }>)
  if (!_.isEmpty(errors)) {
    yield put(addFieldsErrors(errors as Record<string, string>))
    return
  }

  const d = data as ShowcaseFormData
  const showcaseData: Record<string, unknown> = {
    name: '',
    body: d.body?.value ?? '',
    tags: _.uniq(extractHashtags(String(d.body?.value ?? ''))),
    file_url: d.fileUrl?.value ?? '',
    is_static: d.isStatic?.value ?? false,
    metadata: {
      file_type: d.fileType?.value ?? '',
      author: d.author?.value ?? '',
    },
  }

  if (d.id?.value) {
    ;(showcaseData.metadata as Record<string, unknown>).original_post_id = d.id.value
  }

  if (d.slug?.value) {
    ;(showcaseData.metadata as Record<string, unknown>).original_post_slug = d.slug.value
  }

  if (d.authorPhotoUrl?.value) {
    ;(showcaseData.metadata as Record<string, unknown>).author_photo_url = d.authorPhotoUrl.value
  }

  const trackingData = {
    body: showcaseData.body,
    author: (showcaseData.metadata as Record<string, unknown>).author,
    file_type: (showcaseData.metadata as Record<string, unknown>).file_type,
    file_url: showcaseData.file_url,
    tags: showcaseData.tags,
    is_static: showcaseData.is_static,
  }

  const result = (yield call(postShowcaseService.requestSaga as (opts: unknown) => Generator, {
    payload: { data: showcaseData },
  })) as { error?: { response?: { data?: { message?: string } } }; slug?: string }
  const message = _.get(result, 'error.response.data.message')
  if (!_.isEmpty(message)) {
    yield put(addFieldError({ field: 'base', error: message }))
    return
  }

  mixpanel.track(EVENTS.UPLOAD_SHOWCASE, {
    ...trackingData,
    showcase_slug: result?.slug,
  })

  yield* loadShowcasesSaga()
  yield put(changeModal({ isOpen: false }))
}

export function* validateFieldSaga({
  payload: { field, value, validations },
}: {
  payload: { field: string; value: unknown; validations: string }
}): Generator<unknown, void, unknown> {
  const error = validateField({ field, value, fieldValidations: validations })

  if (error) {
    yield put(addFieldError({ field, error: error as string }))
  } else {
    yield put(clearFieldError({ field }))
  }
}

type SubmitShowcaseAction = {
  type: typeof SUBMIT_SHOWCASE
  payload: Record<string, { value: unknown }>
}
type ValidateShowcaseFieldAction = {
  type: typeof VALIDATE_SHOWCASE_FIELD
  payload: { field: string; value: unknown; validations: string }
}

export default function* saga(): Generator<unknown, void, unknown> {
  yield all([
    takeLatest<SubmitShowcaseAction>(SUBMIT_SHOWCASE, submitShowcaseSaga),
    takeEvery<ValidateShowcaseFieldAction>(VALIDATE_SHOWCASE_FIELD, validateFieldSaga),
  ])
}
